import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { SBA_CONFIG } from "@/pages/assessment/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, Check, Loader2, ShieldCheck, Clock, HeartHandshake,
  CheckCircle2, CloudCheck,
} from "lucide-react";

const US_STATES = ["AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY", "DC"];

function captureAcquisition() {
  const p = new URLSearchParams(window.location.search);
  return {
    source: p.get("source") || p.get("utm_source") || null,
    campaign: p.get("campaign") || p.get("utm_campaign") || null,
    landing_page: window.location.pathname,
    utm_source: p.get("utm_source"),
    utm_medium: p.get("utm_medium"),
    utm_campaign: p.get("utm_campaign"),
    utm_content: p.get("utm_content"),
    referrer: document.referrer || null,
    producer_ref: p.get("ref") || p.get("producer_ref") || null,
  };
}

const cacheKey = (t) => `sba_assess_${t}`;
const evalCond = (conds, answers) => {
  if (!conds || !conds.length) return true;
  return conds.every((c) => {
    const v = answers[c.question_id];
    switch (c.op) {
      case "eq": return v === c.value;
      case "neq": return v !== c.value;
      case "in": return Array.isArray(c.value) ? c.value.includes(v) : false;
      case "contains": {
        // multi_select containment: show when the answer list includes ANY listed value.
        const arr = Array.isArray(v) ? v : [];
        const want = Array.isArray(c.value) ? c.value : [c.value];
        return want.some((x) => arr.includes(x));
      }
      case "not_contains": {
        const arr = Array.isArray(v) ? v : [];
        const want = Array.isArray(c.value) ? c.value : [c.value];
        return !want.some((x) => arr.includes(x));
      }
      case "exists": return v !== undefined && v !== null && v !== "";
      default: return true;
    }
  });
};
const isEmpty = (v) => v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);

export default function SBAAssessment({ config = SBA_CONFIG }) {
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [step, setStep] = useState(config.skipWelcome ? 1 : 0); // 0 = welcome; 1..N = sections
  const [answers, setAnswers] = useState({});
  const [resumeToken, setResumeToken] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [consent, setConsent] = useState(false);

  // Load template + optionally resume.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/assessment/template", { params: { brand: config.brandCode, code: config.templateCode } });
        setTemplate(data);
      } catch (e) {
        setLoadErr("This assessment isn't available right now. Please try again shortly.");
        return;
      }
      const token = new URLSearchParams(window.location.search).get("resume");
      if (token) {
        setResumeToken(token);
        try {
          const { data: r } = await api.post("/public/assessment/resume", { resume_token: token });
          const cached = JSON.parse(sessionStorage.getItem(cacheKey(token)) || "null");
          if (cached?.answers) setAnswers(cached.answers);
          if (cached?.consent) setConsent(!!cached.consent);
          if (r?.assessment?.status === "completed") { setStep(1); }
          else if (cached?.step) setStep(cached.step);
          else setStep(1);
          toast.success("Welcome back — your progress was restored.");
        } catch (e) {
          toast.error("We couldn't find that saved session. You can start fresh.");
        }
      } else {
        // Fresh start from the SBA landing CTAs: preserve mode + ZIP.
        const params = new URLSearchParams(window.location.search);
        const m = params.get("mode");
        const z = params.get("zip");
        const seed = {};
        if (z) seed.q_zip = z;
        if (m === "self" || m === "advisor_assisted") seed.q_advisor_pref = m;
        if (Object.keys(seed).length) setAnswers((a) => ({ ...seed, ...a }));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sections = useMemo(() => {
    if (!template) return [];
    return [...(template.sections || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [template]);

  // Sections visible given current answers (section-level branching).
  const visibleSections = useMemo(
    () => sections.filter((s) => evalCond(s.show_if, answers)),
    [sections, answers]
  );
  const totalSteps = visibleSections.length;
  const sectionIdx = step - 1; // 0-based into visibleSections
  const section = step >= 1 ? visibleSections[sectionIdx] : null;
  const mode = answers["q_advisor_pref"] === "advisor_assisted" ? "advisor_assisted" : "self";

  const visibleQuestions = useMemo(
    () => (section ? (section.questions || []).filter((q) => evalCond(q.show_if, answers)) : []),
    [section, answers]
  );

  const setAns = (qid, val) => setAnswers((a) => ({ ...a, [qid]: val }));

  const cacheLocal = (tok, stepVal) => {
    if (!tok) return;
    try { sessionStorage.setItem(cacheKey(tok), JSON.stringify({ answers, consent, step: stepVal })); } catch {}
  };

  const answersFor = (qids) =>
    qids.filter((id) => !isEmpty(answers[id])).map((id) => ({ question_id: id, value: answers[id] }));

  const isContact = section?.key === "contact";
  // Specific missing items so the person knows exactly what is left (never a silent disabled button).
  const missing = useMemo(() => {
    if (!section) return [];
    const out = visibleQuestions.filter((q) => q.required && isEmpty(answers[q.id])).map((q) => q.prompt);
    // A ZIP question requires a CONFIRMED county before continuing (ZCTA match is provisional).
    const zipQ = visibleQuestions.find((q) => q.type === "zip");
    if (zipQ && !isEmpty(answers[zipQ.id]) && isEmpty(answers["_county"])) out.push("Confirm your county (tap the county button under your ZIP)");
    if (isContact) {
      if (isEmpty(answers["q_phone"]) && isEmpty(answers["q_email"])) out.push("A phone or email");
      if (!consent) out.push("Agree to be contacted");
    }
    return out;
  }, [section, visibleQuestions, answers, isContact, consent]);
  const canContinue = missing.length === 0;

  const storePrefill = (tok) => {
    try {
      const fn = (answers["q_first_name"] || "").toString().trim();
      const ln = (answers["q_last_name"] || "").toString().trim();
      const name = [fn, ln].filter(Boolean).join(" ");
      sessionStorage.setItem("sba_prefill_" + tok, JSON.stringify({ name, email: (answers["q_email"] || "").toString().trim() }));
    } catch { /* ignore */ }
  };

  // Persist a section: create on first section, PATCH thereafter.
  const persistSection = async (nextStep) => {
    const ids = visibleQuestions.map((q) => q.id);
    const pct = Math.round((Math.min(nextStep - 1, totalSteps) / totalSteps) * 100);
    setBusy(true); setSaved(false);
    try {
      if (!resumeToken) {
        const body = {
          brand_code: config.brandCode, template_code: config.templateCode,
          person: {
            first_name: (answers["q_first_name"] || "").toString().trim() || "Friend",
            last_name: (answers["q_last_name"] || "").toString().trim() || null,
            role_in_household: "self",
            zip_code: answers["q_zip"] || null,
            state: answers["q_state"] || null,
            county: (answers["_county"] || "").toString().trim() || null,
          },
          mode, needs: [], consent_to_contact: false,
          acquisition: captureAcquisition(),
          answers: answersFor(ids),
        };
        const { data } = await api.post("/public/assessment", body);
        setResumeToken(data.resume_token);
        storePrefill(data.resume_token);
        const url = new URL(window.location.href);
        url.searchParams.set("resume", data.resume_token);
        window.history.replaceState({}, "", url);
        cacheLocal(data.resume_token, nextStep);
      } else {
        await api.patch(`/public/assessment/${resumeToken}`, {
          answers: answersFor(ids),
          current_section: section?.key,
          progress_pct: pct,
        });
        cacheLocal(resumeToken, nextStep);
      }
      setSaved(true);
    } catch (e) {
      // Never lose local answers on a transient failure.
      toast.error("We couldn't save just now — your answers are safe. We'll try again.");
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const onContinue = async () => {
    if (!canContinue) return;
    const next = step + 1;
    try { await persistSection(next); } catch { return; }
    if (next - 1 >= totalSteps) { await onComplete(); return; }
    setStep(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onBack = () => {
    if (step <= 1) { if (config.skipWelcome) return; setStep(0); return; }
    setStep((s) => s - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onComplete = async () => {
    const ids = visibleQuestions.map((q) => q.id);
    setBusy(true);
    try {
      await api.post(`/public/assessment/${resumeToken}/complete`, {
        answers: answersFor(ids),
        consent_to_contact: consent,
        progress_pct: 100,
      });
      storePrefill(resumeToken);
      try { sessionStorage.removeItem(cacheKey(resumeToken)); } catch {}
      navigate(`${config.resultsPath}?resume=${encodeURIComponent(resumeToken)}`);
    } catch (e) {
      toast.error("We couldn't finish just now — your answers are safe. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  // ---------------- Render ----------------
  if (loadErr) {
    return (
      <Shell config={config}>
        <div className="max-w-lg mx-auto text-center py-24" data-testid="assessment-load-error">
          <p className="text-lg text-[#4B5563]">{loadErr}</p>
          <Link to={config.homePath}><Button className="mt-6 rounded-full bg-[#0B192C] text-white h-12 px-6">{config.homeBtnLabel}</Button></Link>
        </div>
      </Shell>
    );
  }
  if (!template) {
    return <Shell config={config}><div className="py-32 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#1E3E62]" /></div></Shell>;
  }

  // Welcome (Section 0)
  if (step === 0) {
    return (
      <Shell config={config}>
        <div className="max-w-2xl mx-auto py-10 sm:py-16" data-testid="assessment-welcome">
          <span className="inline-flex items-center gap-2 text-[#2D6A4F] text-sm font-bold tracking-wide uppercase">
            <Clock className="w-4 h-4" /> {config.welcome.eyebrow}
          </span>
          <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-[#0B192C] mt-4 leading-[1.1]">
            {config.welcome.title}
          </h1>
          <p className="mt-5 text-lg text-[#4B5563] leading-relaxed">
            {config.welcome.subtitle}
          </p>
          <ul className="mt-7 space-y-3">
            {config.welcome.bullets.map((t) => (
              <li key={t} className="flex items-center gap-3 text-base text-[#111827]"><CheckCircle2 className="w-5 h-5 text-[#2D6A4F] shrink-0" /> {t}</li>
            ))}
          </ul>
          <Button size="lg" onClick={() => setStep(1)} data-testid="assessment-start-btn"
            className="mt-9 h-14 px-8 text-lg font-bold rounded-full bg-[#D4AF37] text-[#0B192C] hover:bg-[#B89620]">
            {config.welcome.startBtn} <ArrowRight className="w-5 h-5 ml-1" />
          </Button>
          <p className="mt-5 text-sm text-[#6B7280] flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#2D6A4F]" /> {config.welcome.trustline}</p>
        </div>
      </Shell>
    );
  }

  const pct = Math.round(((step - 1) / totalSteps) * 100);
  return (
    <Shell config={config}>
      <div className="max-w-2xl mx-auto py-6 sm:py-10">
        {/* Progress */}
        <div className="mb-6" data-testid="assessment-progress">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-[#1E3E62]" data-testid="assessment-step-label">Step {step} of {totalSteps}</span>
            {saved && <span className="text-xs text-[#2D6A4F] inline-flex items-center gap-1" data-testid="assessment-saved-indicator"><CloudCheck className="w-3.5 h-3.5" /> Progress saved</span>}
          </div>
          <Progress value={pct} className="h-2" aria-label={`Progress: ${pct} percent`} />
        </div>

        <h2 className="font-display font-bold text-2xl sm:text-3xl text-[#0B192C] tracking-tight" data-testid="assessment-section-title">{section?.title}</h2>

        <div className="mt-6 space-y-8" role="group" aria-label={section?.title}>
          {visibleQuestions.map((q) => (
            q.type === "zip" ? (
              <ZipCountyField key={q.id} q={q}
                zip={answers[q.id]} onZip={(v) => setAns(q.id, v)}
                state={answers["q_state"] || undefined}
                county={answers["_county"]} countyStatus={answers["_county_status"]}
                onCounty={(c, s) => setAnswers((a) => ({ ...a, _county: c, _county_status: s }))}
                onState={(st) => setAnswers((a) => ({ ...a, q_state: st, _state_source: "resolved", _resolved_state: st, _state_conflict: false }))}
                onZipChanged={() => setAnswers((a) => (a._state_source === "resolved"
                  ? { ...a, q_state: "", _state_source: null, _resolved_state: null, _state_conflict: false }
                  : { ...a, _state_conflict: false }))} />
            ) : q.id === "q_state" ? (
              <div key={q.id}>
                <QuestionField q={q} value={answers[q.id]}
                  onChange={(v) => setAnswers((a) => ({ ...a, q_state: v, _state_source: "manual", _state_conflict: !!a._resolved_state && v !== a._resolved_state }))} />
                {answers._state_conflict && (
                  <p className="mt-2 text-sm text-[#B45309]" data-testid="state-zip-conflict" role="status">
                    This state doesn't match your ZIP's location ({answers._resolved_state}). Please re-check your ZIP or state before continuing.
                  </p>
                )}
              </div>
            ) : q.id === "q_additional_coverage" ? (
              <QuestionField key={q.id} value={answers[q.id]} onChange={(v) => setAns(q.id, v)}
                q={{ ...q, exclusive_values: ["none"], exclusive_groups: [["medicaid", "unsure_medicaid"]] }} />
            ) : (
              <QuestionField key={q.id} q={q} value={answers[q.id]} onChange={(v) => setAns(q.id, v)} />
            )
          ))}

          {isContact && (
            <label className="flex items-start gap-3 cursor-pointer rounded-xl border-2 border-[#E5E5E0] bg-white p-4 hover:border-[#BCCCDC]">
              <Checkbox checked={consent} onCheckedChange={(c) => setConsent(!!c)} className="mt-0.5" data-testid="assessment-consent-checkbox" />
              <span className="text-sm text-[#4B5563] leading-relaxed" data-testid="assessment-consent-text">{config.consent[mode]}</span>
            </label>
          )}
        </div>

        <div className="flex justify-between mt-10 pt-6 border-t border-[#E5E5E0]">
          <Button variant="outline" onClick={onBack} className="rounded-full h-12 px-6 text-base border-[#BCCCDC]" data-testid="assessment-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <Button onClick={onContinue} disabled={!canContinue || busy}
            className="rounded-full h-12 px-7 text-base font-bold bg-[#0B192C] text-white hover:bg-[#1E3E62]" data-testid="assessment-continue-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (
              sectionIdx + 1 >= totalSteps
                ? (<><ShieldCheck className="w-4 h-4 mr-1" /> Submit securely</>)
                : (<>Continue <ArrowRight className="w-4 h-4 ml-1" /></>)
            )}
          </Button>
        </div>
        {!canContinue && (
          <p className="mt-3 text-sm text-[#B45309]" data-testid="assessment-validation-hint" role="status">
            To continue, please complete: {missing.join(" · ")}
          </p>
        )}
      </div>
    </Shell>
  );
}

function Shell({ children, config = SBA_CONFIG }) {
  const Logo = config.Logo;
  return (
    <div className="min-h-screen bg-[#FBFBF9] text-[#111827]">
      <header className="sticky top-0 z-30 bg-[#FBFBF9]/90 backdrop-blur border-b border-[#E5E5E0]">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 h-20 flex items-center justify-between">
          <Link to={config.homePath} aria-label={config.logoAria}><Logo className={config.logoClass} /></Link>
          <span className="hidden sm:inline-flex items-center gap-2 text-[#1E3E62] font-semibold text-sm">
            <HeartHandshake className="w-4 h-4 text-[#2D6A4F]" /> {config.wizardTag}
          </span>
        </div>
      </header>
      <main className="px-4 sm:px-8">{children}</main>
    </div>
  );
}

function ZipCountyField({ q, zip, onZip, state, county, countyStatus, onCounty, onState, onZipChanged }) {
  const [status, setStatus] = useState(countyStatus || null);
  const [candidates, setCandidates] = useState([]);
  const [suggested, setSuggested] = useState(null);
  const [loading, setLoading] = useState(false);
  const reqSeq = useRef(0);
  const zip5 = (zip || "").replace(/\D/g, "").slice(0, 5);
  const confirmed = ["confirmed_single", "confirmed_multiple"].includes(status);
  const manualMode = ["unknown", "invalid", "error", "manual"].includes(status);

  const resolve = async (z) => {
    const seq = ++reqSeq.current;
    setLoading(true); setSuggested(null); setCandidates([]);
    try {
      const { data } = await api.post("/location/resolve-zip", { zip_code: z, state: state || undefined });
      if (seq !== reqSeq.current) return;  // a newer ZIP superseded this response — ignore stale result
      setStatus(data.status);
      if (data.status === "single") { setSuggested(data.county); onCounty("", "single"); }
      else if (data.status === "multiple") { setCandidates(data.counties || []); onCounty("", "multiple"); }
      else { onCounty("", data.status); }
      // Prefill state from the resolver when it KNOWS one (single or multi-county). State is
      // unambiguous even when county isn't; never hardcoded, never inferred from county/IP.
      if (data.state && onState) onState(data.state);
    } catch (e) { if (seq === reqSeq.current) { setStatus("error"); onCounty("", "error"); } }
    finally { if (seq === reqSeq.current) setLoading(false); }
  };

  useEffect(() => {
    if (zip5.length === 5 && isEmpty(county)
        && !["single", "multiple", "unknown", "invalid", "manual", "error",
             "confirmed_single", "confirmed_multiple"].includes(status)) {
      resolve(zip5);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zip5]);

  const reset = () => { onCounty("", null); setStatus(null); setSuggested(null); setCandidates([]); if (zip5.length === 5) resolve(zip5); };

  return (
    <div data-testid={`q-${q.id}`}>
      <Label className="block font-display font-bold text-lg text-[#0B192C]" htmlFor={q.id}>
        {q.prompt}{q.required && <span className="text-[#B45309]"> *</span>}
      </Label>
      {q.help_text && <p className="text-sm text-[#6B7280] mt-1">{q.help_text}</p>}
      <Input id={q.id} value={zip || ""} data-testid="zip-input" maxLength={5} inputMode="numeric"
        onChange={(e) => { if (e.target.value.replace(/\D/g, "").slice(0, 5) === zip5) { onZip(e.target.value); return; } reqSeq.current++; onZip(e.target.value); onCounty("", null); setStatus(null); setSuggested(null); setCandidates([]); if (onZipChanged) onZipChanged(); }}
        className="mt-3 h-12 text-base bg-white max-w-md" />

      {loading && (
        <p className="mt-3 text-sm text-[#1E3E62] inline-flex items-center gap-2" data-testid="zip-county-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Looking up your county…
        </p>
      )}

      {!loading && confirmed && !isEmpty(county) && (
        <div className="mt-3 rounded-xl border-2 border-[#D4AF37] bg-[#FAF6E8] p-3 flex items-center justify-between" data-testid="zip-county-chosen">
          <span className="text-base font-semibold text-[#0B192C] inline-flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-[#2D6A4F]" /> {county} County
          </span>
          <button type="button" onClick={reset} className="text-sm text-[#1E3E62] underline" data-testid="zip-county-change">Change</button>
        </div>
      )}

      {!loading && !confirmed && status === "single" && suggested && (
        <div className="mt-3 rounded-xl border-2 border-[#BCCCDC] bg-white p-3" data-testid="zip-county-suggested">
          <p className="text-sm text-[#4B5563]">Based on public ZIP-area data, you appear to be in <span className="font-semibold text-[#0B192C]">{suggested} County</span>. Please confirm.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button type="button" onClick={() => { onCounty(suggested, "confirmed_single"); setStatus("confirmed_single"); }} className="rounded-full h-10 px-4 bg-[#0B192C] text-white hover:bg-[#1E3E62]" data-testid="zip-county-confirm">Yes, {suggested} County</Button>
            <Button type="button" variant="outline" onClick={() => setStatus("manual")} className="rounded-full h-10 px-4 border-[#BCCCDC]" data-testid="zip-county-different">It's a different county</Button>
          </div>
        </div>
      )}

      {!loading && !confirmed && status === "multiple" && candidates.length > 0 && (
        <div className="mt-3 rounded-xl border-2 border-[#BCCCDC] bg-white p-3" data-testid="zip-county-multiple">
          <p className="text-sm text-[#4B5563]">ZIP {zip5} covers more than one county. Which county are you in?</p>
          <div className="mt-3 grid gap-2">
            {candidates.map((c) => (
              <button key={c.county_fips} type="button" onClick={() => { onCounty(c.county, "confirmed_multiple"); setStatus("confirmed_multiple"); }}
                data-testid={`zip-county-option-${c.county_fips}`}
                className="text-left min-h-[48px] px-4 rounded-xl border-2 border-[#E5E5E0] bg-white text-[#1E3E62] font-semibold hover:border-[#1E3E62]">{c.county} County</button>
            ))}
          </div>
        </div>
      )}

      {!loading && !confirmed && manualMode && (
        <div className="mt-3" data-testid="zip-county-manual">
          <Label className="block text-sm font-semibold text-[#0B192C]">Your county</Label>
          <Input value={county || ""} onChange={(e) => onCounty(e.target.value, "manual")}
            placeholder="Enter your county" className="mt-1.5 h-12 text-base bg-white max-w-md" data-testid="zip-county-manual-input" />
        </div>
      )}

      <p className="mt-2 text-xs text-[#6B7280]" data-testid="zip-county-disclaimer">
        County is derived from public ZIP-area (ZCTA) data and is provisional until we verify your address.
      </p>
    </div>
  );
}

function QuestionField({ q, value, onChange }) {
  const label = (
    <Label className="block font-display font-bold text-lg text-[#0B192C]" htmlFor={q.id}>
      {q.prompt}{q.required && <span className="text-[#B45309]"> *</span>}
    </Label>
  );
  const help = q.help_text ? <p className="text-sm text-[#6B7280] mt-1">{q.help_text}</p> : null;

  if (q.type === "single_select") {
    return (
      <fieldset data-testid={`q-${q.id}`}>
        <legend className="font-display font-bold text-lg text-[#0B192C]">{q.prompt}{q.required && <span className="text-[#B45309]"> *</span>}</legend>
        {help}
        <div className="grid sm:grid-cols-2 gap-3 mt-3" role="radiogroup" aria-label={q.prompt}>
          {(q.options || []).map((o) => {
            const on = value === o.value;
            return (
              <button key={o.value} type="button" role="radio" aria-checked={on}
                onClick={() => onChange(o.value)} data-testid={`q-${q.id}-opt-${o.value}`}
                className={`text-left min-h-[56px] px-4 rounded-2xl border-2 text-base font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1E3E62] ${on ? "border-[#D4AF37] bg-[#FAF6E8] text-[#0B192C]" : "border-[#E5E5E0] bg-white text-[#1E3E62] hover:border-[#BCCCDC]"}`}>
                <span className="inline-flex items-center gap-2">{on && <CheckCircle2 className="w-5 h-5 text-[#2D6A4F]" />}{o.label}</span>
              </button>
            );
          })}
        </div>
      </fieldset>
    );
  }

  if (q.type === "multi_select" || q.type === "chips") {
    const arr = Array.isArray(value) ? value : [];
    const maxSelect = Number.isInteger(q.max_select) && q.max_select > 0 ? q.max_select : null;
    const atCap = maxSelect !== null && arr.length >= maxSelect;
    const EX = Array.isArray(q.exclusive_values) ? q.exclusive_values : [];
    const groups = Array.isArray(q.exclusive_groups) ? q.exclusive_groups : [];
    const toggle = (v) => {
      if (arr.includes(v)) { onChange(arr.filter((x) => x !== v)); return; }
      if (EX.includes(v)) { onChange([v]); return; }                 // exclusive (e.g. "None") clears all others
      let next = [...arr.filter((x) => !EX.includes(x)), v];         // adding a real option clears any exclusive
      for (const g of groups) { if (g.includes(v)) next = next.filter((x) => x === v || !g.includes(x)); }
      if (maxSelect !== null && next.length > maxSelect) return;     // respect the max_select cap
      onChange(next);
    };
    return (
      <fieldset data-testid={`q-${q.id}`}>
        <legend className="font-display font-bold text-lg text-[#0B192C]">{q.prompt}{q.required && <span className="text-[#B45309]"> *</span>}</legend>
        {help}
        {maxSelect !== null && (
          <p className="text-sm text-[#6B7280] mt-1" data-testid={`q-${q.id}-counter`} role="status" aria-live="polite">
            {arr.length} of {maxSelect} selected{atCap ? " — maximum reached" : ""}
          </p>
        )}
        <div className="flex flex-wrap gap-2.5 mt-3">
          {(q.options || []).map((o) => {
            const on = arr.includes(o.value);
            const locked = !on && atCap;
            return (
              <button key={o.value} type="button" role="checkbox" aria-checked={on}
                aria-disabled={locked} disabled={locked}
                onClick={() => toggle(o.value)} data-testid={`q-${q.id}-opt-${o.value}`}
                className={`min-h-[48px] px-5 rounded-full border-2 text-base font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1E3E62] ${on ? "border-[#D4AF37] bg-[#D4AF37] text-[#0B192C]" : locked ? "border-[#E5E5E0] bg-[#F3F3EF] text-[#9CA3AF] cursor-not-allowed" : "border-[#BCCCDC] bg-white text-[#1E3E62] hover:border-[#1E3E62]"}`}>
                {on && <Check className="w-4 h-4 mr-1 inline -mt-0.5" />}{o.label}
              </button>
            );
          })}
        </div>
      </fieldset>
    );
  }

  if (q.type === "state") {
    return (
      <div data-testid={`q-${q.id}`}>
        {label}{help}
        <Select value={value || ""} onValueChange={onChange}>
          <SelectTrigger id={q.id} className="mt-3 h-12 text-base bg-white max-w-xs" data-testid={`q-${q.id}-trigger`}><SelectValue placeholder="Select your state" /></SelectTrigger>
          <SelectContent className="max-h-64">{US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    );
  }

  // zip / short_text
  return (
    <div data-testid={`q-${q.id}`}>
      {label}{help}
      <Input id={q.id} value={value || ""} onChange={(e) => onChange(e.target.value)}
        maxLength={q.type === "zip" ? 5 : undefined}
        inputMode={q.type === "zip" ? "numeric" : undefined}
        className="mt-3 h-12 text-base bg-white max-w-md" data-testid={`q-${q.id}-input`} />
    </div>
  );
}
